<template>
	<div>
		<DashboardHeader
			:title="$route.name"
			:buttonItems="buttonItems"
			@click="handleHeaderClick"
		/>
		<div class="container-fluid mt--6">
			<b-overlay :show="isLoading" rounded="sm" no-wrap />
			<Card no-body>
				<b-row align-v="center" slot="header">
					<b-col cols="8">
						<h3 class="mb-0">All Products</h3>
					</b-col>
					<b-col cols="4" class="text-right">
						<h3 class="mb-0">{{ allProducts.length }} Total</h3>
					</b-col>
				</b-row>
				<b-row v-if="allProducts.length === 0 && !isLoading" class="m-3">
					<b-col class="text-center text-muted">
						No products yet. Click <strong>New Product</strong> to add one.
					</b-col>
				</b-row>
				<b-row class="m-2">
					<div
						v-for="item in allProducts"
						:key="item.productId"
						class="col-sm-4 col-md-3 p-1"
					>
						<ConferenceProductCard
							:product="item"
							:dataSets="dataSets"
							@edit="onEdit"
							@delete="onDelete"
							@archive="onArchive"
						/>
					</div>
				</b-row>
			</Card>
		</div>

		<NewConferenceProduct
			:headerTitle="modalTitle"
			:showModal="showModal"
			:editProduct="editTarget"
			@close="onModalClose"
			@saved="onSaved"
		/>
	</div>
</template>
<script>
import {
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	orderBy,
	query,
	serverTimestamp,
	setDoc,
} from '@firebase/firestore';
import { mapActions, mapGetters } from 'vuex';
import { getOrganizationFirestore } from '../../../resources/organizationFirebase';
import Card from '../../../components/Cards/Card.vue';
import DashboardHeader from '../HeaderFooterComponents/DashboardHeader.vue';
import ConferenceProductCard from '../../Components/ShopViews/ConferenceProductCard.vue';
import NewConferenceProduct from '../../mainForms/NewConferenceProduct.vue';

export default {
	name: 'ConferenceProducts',
	components: { Card, DashboardHeader, ConferenceProductCard, NewConferenceProduct },
	data() {
		return {
			isLoading: true,
			allProducts: [],
			// Loaded from account/appInformation.dataSet — passed down to each
			// ConferenceProductCard so it can resolve the product's dataSet id
			// to a human-readable conference name for the badge.
			dataSets: [],
			showModal: false,
			editTarget: null,
			buttonItems: [
				{ index: 0, title: 'New Product', type: 'primary', icon: 'fas fa-plus' },
			],
		};
	},
	computed: {
		...mapGetters({ organization: 'getOrganization' }),
		modalTitle() {
			return this.editTarget ? 'Edit Conference Product' : 'New Conference Product';
		},
	},
	methods: {
		...mapActions(['showNotification']),
		handleHeaderClick(button) {
			if (button.index === 0) {
				this.editTarget = null;
				this.showModal = true;
			}
		},
		getProducts() {
			this.isLoading = true;
			const firestore = getOrganizationFirestore(this.organization);
			const attendeeProductsRef = query(
				collection(firestore, 'products'),
				orderBy('index', 'desc')
			);
			const exhibitorProductsRef = query(
				collection(firestore, 'exhibitorProducts'),
				orderBy('index', 'desc')
			);
			Promise.all([getDocs(attendeeProductsRef), getDocs(exhibitorProductsRef)])
				.then(([attendeeSnapshot, exhibitorSnapshot]) => {
					const attendeeProducts = attendeeSnapshot.docs.map((d) => ({
						...d.data(),
						sourceCollection: 'products',
					}));
					const exhibitorProducts = exhibitorSnapshot.docs.map((d) => ({
						...d.data(),
						checkoutAudience: 'exhibitor',
						sourceCollection: 'exhibitorProducts',
					}));
					this.allProducts = attendeeProducts
						.concat(exhibitorProducts)
						.sort((a, b) => (b.index || 0) - (a.index || 0));
				})
				.catch((error) => {
					this.showNotification({ type: 'danger', message: error.message });
				})
				.finally(() => {
					this.isLoading = false;
				});
		},
		// Loaded once for the page; ConferenceProductCard receives the array
		// via prop and resolves its product.dataSet → matching title.
		async loadDataSets() {
			try {
				const firestore = getOrganizationFirestore(this.organization);
				const snap = await getDoc(doc(firestore, 'account/appInformation'));
				if (!snap.exists()) return;
				const data = snap.data();
				this.dataSets = Array.isArray(data.dataSet) ? data.dataSet : [];
			} catch (e) {
				// Non-fatal — cards just won't show the conference badge.
			}
		},
		onEdit(product) {
			this.editTarget = product;
			this.showModal = true;
		},
		onDelete(product) {
			const firestore = getOrganizationFirestore(this.organization);
			const sourceCollection =
				product.sourceCollection === 'exhibitorProducts'
					? 'exhibitorProducts'
					: 'products';
			const productRef = doc(firestore, `${sourceCollection}/${product.productId}`);
			deleteDoc(productRef)
				.then(() => {
					this.allProducts = this.allProducts.filter(
						(p) => p.productId !== product.productId
					);
					this.showNotification({
						type: 'success',
						message: `${product.productTitle} deleted.`,
					});
				})
				.catch((err) => {
					this.showNotification({ type: 'danger', message: err.message });
				});
		},
		// Archive = copy the doc into `archive_products` (same id) then delete
		// from `products`. Write the archive copy FIRST so a failure mid-way
		// leaves the product still purchasable in the cart rather than gone.
		// Adds `archivedAt` so we can sort/clean up archived items later.
		async onArchive(product) {
			const firestore = getOrganizationFirestore(this.organization);
			const sourceCollection =
				product.sourceCollection === 'exhibitorProducts'
					? 'exhibitorProducts'
					: 'products';
			const archiveCollection =
				sourceCollection === 'exhibitorProducts'
					? 'archive_exhibitorProducts'
					: 'archive_products';
			const archiveRef = doc(firestore, `${archiveCollection}/${product.productId}`);
			const productRef = doc(firestore, `${sourceCollection}/${product.productId}`);
			try {
				const archivePayload = Object.assign({}, product);
				delete archivePayload.sourceCollection;
				await setDoc(archiveRef, {
					...archivePayload,
					archivedAt: serverTimestamp(),
				});
				await deleteDoc(productRef);
				this.allProducts = this.allProducts.filter(
					(p) => p.productId !== product.productId
				);
				this.showNotification({
					type: 'success',
					message: `${product.productTitle} archived.`,
				});
			} catch (err) {
				this.showNotification({ type: 'danger', message: err.message });
			}
		},
		onModalClose() {
			this.showModal = false;
			this.editTarget = null;
		},
		onSaved(product) {
			const existingIndex = this.allProducts.findIndex(
				(p) => p.productId === product.productId
			);
			if (existingIndex >= 0) {
				this.$set(this.allProducts, existingIndex, Object.assign({}, this.allProducts[existingIndex], product));
				this.showNotification({
					type: 'success',
					message: `${product.productTitle} updated.`,
				});
			} else {
				this.allProducts.push(product);
				this.showNotification({
					type: 'success',
					message: `${product.productTitle} added.`,
				});
			}
		},
	},
	mounted() {
		this.getProducts();
		this.loadDataSets();
	},
};
</script>
